import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { StakeholderEmail } from '../model/stakeholder-email';
import { Signature } from '../model/signature';
import { from, Observable, of, throwError } from 'rxjs';
import { tap, catchError, map, mergeMap } from 'rxjs/operators';
import { TokenService } from './token.service';
import { EmailStatus } from '../model/emailstatus';
import { Store } from '@ngxs/store';
import * as ProcessingActions from "../../state/processing.actions";
import { HttpCommonService } from './http-common.service';
import { Token } from '../model/token';


@Injectable({
  providedIn: 'root'
})
export class EmailService {

  private readonly emailUrl = environment.api + '/stakeholderemail';
  private readonly pimaApiSignatureUrl = environment.api + '/lettersignature';

  constructor(
    private http: HttpClient,
    private store: Store,
    private httpCommonService:  HttpCommonService,
    private tokenService: TokenService) { }

  getEmailById(id: string): Observable<StakeholderEmail> {
    const url = `${this.emailUrl}/${id}`;
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.tokenService.getToken()}` }),
      withCredentials: true
    };

    return this.http.get<StakeholderEmail>(url, httpOptions).pipe
      (
        tap(_ => console.log(`fetched email id=${id}`)),
        catchError(this.httpCommonService.basicErrorHandler)
      );
  }

  getSignatures(): Observable<Signature[]> {
    const url = `${this.pimaApiSignatureUrl}/get`;
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.tokenService.getToken()}` }),
      withCredentials: true
    };


    return this.http.get<Signature[]>(url, httpOptions).pipe
      (
        map(_ => {
          console.log(`fetched signatures`);
          let sigs: Signature[] = [];

          for (var s in _) {
            let sig = new Signature();
            sig.img = _[s]['img'];
            sig.text = _[s]['text'];
            sig.id = _[s]['id'];
            sigs.push(sig);
          }

          return sigs;
        }),
        //tap(_ => console.log(`fetched signatures`)),
        catchError(this.httpCommonService.basicErrorHandler)
      );
  }

  queryEmail(skip: number = 0, top: number = 0, projectId: number = 0): Observable<StakeholderEmail[]> {
    const url = `${this.emailUrl}/query?skip=${skip}&top=${top}&projectId=${projectId}`;
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.tokenService.getToken()}` }),
      withCredentials: true
    };
    this.store.dispatch(new ProcessingActions.Reset());
    this.store.dispatch(new ProcessingActions.SetEmailSending(true));

    return this.http.get<StakeholderEmail[]>(url, httpOptions).pipe
      (
        tap(_ => console.log(`fetched emails`)),
        catchError(this.httpCommonService.basicErrorHandler)
      );
  }

  sendEmail(email: StakeholderEmail, isBatched = false): Observable<StakeholderEmail> {
    // C# api doesn't like body and params that aren't included via the url
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.tokenService.getToken()}` }),
      withCredentials: true
    };
    // Post not passing creds.
    const url = `${this.emailUrl}/send?isBatched=${isBatched}`;
      return this.http.post<StakeholderEmail>(url, email, httpOptions).pipe(
          tap((newEmail: StakeholderEmail) => console.log(`sent email w/ id=${newEmail.Id}`)),
          catchError(this.httpCommonService.basicErrorHandler)
      );
  }

  sendMockEmail(email: StakeholderEmail): Observable<StakeholderEmail> {
    return of(email).pipe(
      tap(testEmail => console.log('sendMockEmail -> ', [testEmail, Date.now()])),
      catchError(this.httpCommonService.basicErrorHandler)
    );

  }

  createEmailBccBatches(email: StakeholderEmail, batchSize: number){
    var bccBatches = email.BccAddresses.reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index/batchSize)

      if(!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = [] // start a new chunk
      }
      resultArray[chunkIndex].push(item)

      return resultArray
    }, []);
    const emailBatches = bccBatches.map(bcc => {
      const batch = JSON.parse(JSON.stringify(email)) as StakeholderEmail;
      batch.BccAddresses = bcc;
      return batch;
    });
    try {
      const batchSizes = emailBatches.map(batch => this.calcBatchSize(batch));
      console.log('Batch Sizes ->', batchSizes);
      return from(emailBatches);
    } catch (error){
      const message = `Batch Size ${error}`;
      return throwError(message);
    }
  }

  createEmail(email: StakeholderEmail): Observable<StakeholderEmail> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.tokenService.getToken()}` }),
      withCredentials: true
    };

    return this.http.post<StakeholderEmail>(this.emailUrl, email, httpOptions).pipe(
      tap((newEmail: StakeholderEmail) => console.log(`created email w/ id=${newEmail.Id}`)),
      catchError(this.httpCommonService.basicErrorHandler)
    );
  }

  updateEmail(email: StakeholderEmail): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.tokenService.getToken()}` }),
      withCredentials: true
    };

    return this.http.put(this.emailUrl + '/' + email.Id, email, httpOptions).pipe(
      tap(_ => console.log(`updated email id=${email.Id}`)),
      catchError(this.httpCommonService.basicErrorHandler)
    );
  }

  deleteEmail(email: StakeholderEmail | number): Observable<StakeholderEmail> {
    const id = typeof email === 'number' ? email : email.Id;
    const url = `${this.emailUrl}/${id}`;

    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.tokenService.getToken()}` }),
      withCredentials: true
    };

    return this.http.delete<StakeholderEmail>(url, httpOptions).pipe(
      tap(_ => console.log(`deleted email id=${id}`)),
      catchError(this.httpCommonService.basicErrorHandler)
    );
  }

  getAuditJson(emailId: string): Observable<EmailStatus[]> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.tokenService.getToken()}` }),
      withCredentials: true
    };
    return this.http.get<EmailStatus[]>(environment.api + '/stakeholderemailstatus/ExportBatch?batchId=' + emailId, httpOptions).pipe(
      tap(batchExportResult => console.log('batch json', batchExportResult)),
      catchError(this.httpCommonService.basicErrorHandler)
    );
  }

  getAudit(emailId: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.tokenService.getToken()}` }),
      withCredentials: true
    };

    return this.http.get(environment.api + '/stakeholderemailstatus/ExportBatchCSV?batchId=' + emailId, httpOptions).pipe(
      tap(batchExportResult => console.log('batch export', batchExportResult)),
      catchError(this.httpCommonService.basicErrorHandler)
    );
  }

  private calcBatchSize(batch: StakeholderEmail){
    // https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/Limits.html
    const dynamoItemKb = 400 - 10;
    const sqsMessageKb = 256 - 10;
    // const dynamoItemKb = 1; // for testing
    // To, cc, bcc are all now stored separately in dynamo
    let smallBatch = JSON.parse(JSON.stringify(batch));
    let bodySQS = {
      RecipientType: smallBatch.RecipientType,
      MessageId: smallBatch.Id,
      Address: "pimatestemail1+stakeid100000@gmail.com",
      StakeId: 99999,
      Header: smallBatch.HtmlStart + smallBatch.Header,
      Message: smallBatch.Message,
      Footer: smallBatch.Footer + smallBatch.HtmlEnd,
      Subject: smallBatch.Subject,
      SentBy: smallBatch.UserId,
      FromDisplay: smallBatch.FromDisplay,
    };
    smallBatch.ToAddresses = [];
    smallBatch.CcAddresses = [];
    smallBatch.BccAddresses = [];
    smallBatch.Recipients = [];
    const size = new TextEncoder().encode(JSON.stringify(smallBatch)).length;
    const messageKiloBytes = size / 1024;
    // check the size of the SQS message its limited to 256kb
    const size2 = new TextEncoder().encode(JSON.stringify(bodySQS)).length;
    const bodyKiloBytes = (size2 * 5) / 1024;

    if (messageKiloBytes > dynamoItemKb){
      throw new Error(`Content Size Exceeded: ${bodyKiloBytes.toFixed(2)} KB > ${dynamoItemKb} KB.  Try reducing the number of Bcc addresses per batch.`)
    }
    if (bodyKiloBytes > sqsMessageKb){
      throw new Error(`Email Body Size Exceeded: ${bodyKiloBytes.toFixed(2)} KB > ${sqsMessageKb} KB.  The email body is too big, try reducing the email content.`)
    }
    const megaBytes = messageKiloBytes / 1024;
    return {
      kb: messageKiloBytes,
      mb: megaBytes,
      body: bodyKiloBytes
    }
  }

}
